import { Anchor, Drawer, Loader, Stack, Table, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedUniqueValues,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';
import type { SortingState } from '@tanstack/table-core';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { TableLayout } from '@apple/ui/layouts/TableLayout';

import { plantQueryOptions } from '@apple/features/plants/queries/queries';
import type {
	ProductSearchHistoryFilters,
	ProductSearchHistoryItem,
	ProductSearchHistoryTableData,
} from '@apple/features/search-history/models/search-history';
import { productSearchHistoryQueries } from '@apple/features/search-history/queries/search-history';

const features = createDataTableFeatures({
	sortedRowModel: createSortedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/reports/product-search-history')({
	component: ProductSearchHistoryRoute,
});

const defaultFilters: ProductSearchHistoryFilters = {
	dateRange: '60',
	startDate: dayjs().subtract(60, 'days').startOf('day').format('YYYY-MM-DD'),
	endDate: dayjs().endOf('day').format('YYYY-MM-DD'),
};
const defaultSorting: SortingState = [{ id: 'count', desc: true }];

function ProductSearchHistoryRoute() {
	const { t } = useTranslation('reports');
	const search = Route.useSearch();
	const navigate = Route.useNavigate();

	const plantsQuery = useQuery(plantQueryOptions.plants);

	const plantCodes = useMemo(
		() => plantsQuery.data?.map(x => x.warehouseCode) ?? [],
		[plantsQuery.data],
	);

	const tableState = useTableState<ProductSearchHistoryTableData, ProductSearchHistoryFilters>({
		search,
		navigate,
		defaultFilters,
		defaultSorting,
		fieldMap: [],
	});

	const columnHelper = createDataTableColumnHelper<
		ProductSearchHistoryTableData,
		ProductSearchHistoryFilters
	>();

	const currentFilterData: ProductSearchHistoryFilters = useMemo(
		() => ({
			...tableState.currentFilterData,
			startDate:
				tableState.currentFilterData.dateRange === 'custom'
					? tableState.currentFilterData.startDate
					: dayjs()
							.subtract(
								Number.parseInt(tableState.currentFilterData.dateRange ?? '60', 10),
								'days',
							)
							.format('YYYY-MM-DD'),
			endDate:
				tableState.currentFilterData.dateRange === 'custom'
					? tableState.currentFilterData.endDate
					: dayjs().format('YYYY-MM-DD'),
		}),
		[tableState.currentFilterData],
	);

	const productSearchHistoryQuery = useQuery(
		productSearchHistoryQueries.search(currentFilterData),
	);

	const columns = useMemo(
		() =>
			columnHelper.columns([
				columnHelper.accessor('searchTerm', {
					id: 'searchTerm',
					header: t('productSearchHistory.columns.searchTerm'),
					enableColumnFilter: false,
					size: 300,
					cell: ({ row }) => (
						<ClickDetails item={row.original} filters={currentFilterData} />
					),
				}),
				columnHelper.accessor('count', {
					id: 'count',
					header: t('productSearchHistory.columns.count'),
					enableColumnFilter: false,
					size: 100,
					cell: ({ row }) => row.original.count.toLocaleString(),
				}),
				columnHelper.accessor('avgResults', {
					id: 'avgResults',
					header: t('productSearchHistory.columns.avgResults'),
					enableColumnFilter: false,
					size: 100,
					cell: ({ row }) => row.original.avgResults.toFixed(1),
				}),
				columnHelper.filterOnly('storeId', {
					group: t('productSearchHistory.filterGroups.details'),
					label: t('productSearchHistory.columns.storeId'),
					variant: 'text',
				}),
				columnHelper.filterOnly('warehouseCode', {
					group: t('productSearchHistory.filterGroups.details'),
					label: t('productSearchHistory.columns.plant'),
					variant: 'select',
					getFilterDisplayValue: value =>
						plantsQuery.data?.find(x => x.warehouseCode === value)?.name ??
						String(value),
				}),
				columnHelper.filterOnly('dateRange', {
					group: t('productSearchHistory.filterGroups.details', 'Details'),
					label: t('productSearchHistory.fields.dateRange'),
					variant: 'select',
					getFilterDisplayValue: value =>
						value === 'custom'
							? t('controls:dataTable.filters.options.dateRange.custom')
							: t('controls:dataTable.filters.options.dateRange.lastNumberOfDays', {
									days: value,
								}),
				}),
				columnHelper.filterOnly('startDate', {
					group: t('productSearchHistory.filterGroups.details', 'Details'),
					label: t('productSearchHistory.fields.startDate'),
					variant: 'date',
					hidden: tableState.currentFilterData.dateRange !== 'custom',
				}),
				columnHelper.filterOnly('endDate', {
					group: t('productSearchHistory.filterGroups.details', 'Details'),
					label: t('productSearchHistory.fields.endDate'),
					variant: 'date',
					hidden: tableState.currentFilterData.dateRange !== 'custom',
				}),
			]),
		[
			columnHelper,
			t,
			tableState.currentFilterData.dateRange,
			currentFilterData,
			plantsQuery.data,
		],
	);

	const table = useTable({
		features,
		data: productSearchHistoryQuery.data ?? [],
		rowCount: productSearchHistoryQuery.data?.length ?? 0,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: false,
		manualPagination: false,
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'warehouseCode':
						return new Map(plantCodes.map(x => [x, 1]));
					case 'dateRange':
						return new Map([
							['30', 1],
							['60', 1],
							['90', 1],
							['custom', 1],
						]);
					case 'region':
						return new Map([
							['US', 1],
							['CA', 1],
						]);
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
		columns,
	});

	return (
		<TableLayout title={t('productSearchHistory.title')} table={table}>
			<DataTable
				table={table}
				variant='apple-table'
				loading={productSearchHistoryQuery.isFetching}
			/>
		</TableLayout>
	);
}

function ClickDetails({
	item,
	filters,
}: {
	item: ProductSearchHistoryItem;
	filters: ProductSearchHistoryFilters;
}) {
	const { t } = useTranslation('reports');
	const [drawerOpened, drawerActions] = useDisclosure(false);

	const topProductsQuery = useQuery({
		...productSearchHistoryQueries.topProducts(filters, item.searchTerm),
		enabled: drawerOpened,
	});

	return (
		<>
			<Anchor size='sm' component='button' type='button' onClick={drawerActions.open}>
				{item.searchTerm}
			</Anchor>

			<Drawer
				title={t('productSearchHistory.columns.clicks.title', {
					searchTerm: item.searchTerm,
				})}
				opened={drawerOpened}
				onClose={() => drawerActions.close()}
				padding='md'
				size='xl'
				position='right'
				offset={24}
				radius='lg'
				overlayProps={{ blur: 0 }}
			>
				{topProductsQuery.isLoading && <Loader size='sm' mt='xs' />}
				{!topProductsQuery.isLoading && topProductsQuery.isError && (
					<Text size='sm' c='red'>
						{t('common:error.generic')}
					</Text>
				)}
				<Stack gap='md'>
					{!topProductsQuery.isLoading &&
						!topProductsQuery.isError &&
						topProductsQuery.data && (
							<Table>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>
											{t('productSearchHistory.columns.clicks.productTitle')}
										</Table.Th>
										<Table.Th>
											{t('productSearchHistory.columns.clicks.productId')}
										</Table.Th>
										<Table.Th>
											{t(
												'productSearchHistory.columns.clicks.selectionCount',
											)}
										</Table.Th>
										<Table.Th>
											{t(
												'productSearchHistory.columns.clicks.averagePosition',
											)}
										</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{topProductsQuery.data.map((click, idx) => (
										<Table.Tr key={idx}>
											<Table.Td>{click.title}</Table.Td>
											<Table.Td>{click.documentId}</Table.Td>
											<Table.Td>{click.selectionCount}</Table.Td>
											<Table.Td>{click.averagePosition.toFixed(1)}</Table.Td>
										</Table.Tr>
									))}
								</Table.Tbody>
							</Table>
						)}
				</Stack>
			</Drawer>
		</>
	);
}
